import { collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

// Google Books API service matching iOS app functionality
class BookService {
  constructor() {
    this.apiBaseUrl = 'https://www.googleapis.com/books/v1/volumes';
  }

  // Search Google Books API (matching iOS implementation)
  async searchGoogleBooks(searchQuery) {
    try {
      const encodedQuery = encodeURIComponent(searchQuery);
      const url = `${this.apiBaseUrl}?q=${encodedQuery}&maxResults=20&langRestrict=en`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.items) {
        return [];
      }

      // Process books to ensure HTTPS URLs (matching iOS security)
      return this.processBooks(data.items);
    } catch (error) {
      console.error('Error searching Google Books:', error);
      throw error;
    }
  }

  // Search by ISBN (matching iOS barcode scanner functionality)
  async searchByISBN(isbn) {
    try {
      const url = `${this.apiBaseUrl}?q=isbn:${isbn}`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.items || data.items.length === 0) {
        return null;
      }

      return this.processBooks([data.items[0]])[0];
    } catch (error) {
      console.error('Error searching by ISBN:', error);
      throw error;
    }
  }

  // Process books to ensure HTTPS URLs (matching iOS implementation)
  processBooks(books) {
    return books.map(book => {
      const volumeInfo = book.volumeInfo || {};
      
      // Convert HTTP to HTTPS for security (matching iOS)
      if (volumeInfo.imageLinks) {
        if (volumeInfo.imageLinks.thumbnail) {
          volumeInfo.imageLinks.thumbnail = volumeInfo.imageLinks.thumbnail.replace('http://', 'https://');
        }
        if (volumeInfo.imageLinks.smallThumbnail) {
          volumeInfo.imageLinks.smallThumbnail = volumeInfo.imageLinks.smallThumbnail.replace('http://', 'https://');
        }
      }

      return {
        id: book.id,
        volumeInfo: {
          title: volumeInfo.title || 'Unknown Title',
          authors: volumeInfo.authors || ['Unknown Author'],
          description: volumeInfo.description || '',
          publishedDate: volumeInfo.publishedDate || '',
          publisher: volumeInfo.publisher || '',
          pageCount: volumeInfo.pageCount || null,
          categories: volumeInfo.categories || [],
          industryIdentifiers: volumeInfo.industryIdentifiers || [],
          imageLinks: volumeInfo.imageLinks || null,
          language: volumeInfo.language || 'en'
        }
      };
    });
  }

  // Convert Google Book to internal Book structure (matching iOS)
  convertGoogleBookToBook(googleBook, userId) {
    const { volumeInfo } = googleBook;
    
    return {
      userId: userId,
      title: volumeInfo.title,
      authors: volumeInfo.authors,
      description: volumeInfo.description,
      imageUrl: volumeInfo.imageLinks?.thumbnail || null,
      readingStatus: 'toRead', // Default status
      pageCount: volumeInfo.pageCount,
      categories: volumeInfo.categories,
      publishedDate: volumeInfo.publishedDate,
      publisher: volumeInfo.publisher,
      language: volumeInfo.language,
      isbn: this.extractISBN(volumeInfo.industryIdentifiers),
      googleBooksId: googleBook.id,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      rating: null,
      mood: null
    };
  }

  // Extract ISBN from industry identifiers (matching iOS)
  extractISBN(identifiers) {
    if (!identifiers) return null;
    
    const isbn13 = identifiers.find(id => id.type === 'ISBN_13');
    if (isbn13) return isbn13.identifier;
    
    const isbn10 = identifiers.find(id => id.type === 'ISBN_10');
    if (isbn10) return isbn10.identifier;
    
    return null;
  }

  // Check for duplicate books before adding
  async checkForDuplicates(userId, title, authors) {
    try {
      const normalizedTitle = (title || 'Unknown Title').toLowerCase().trim();
      const safeAuthors = Array.isArray(authors) ? authors : ['Unknown Author'];
      const normalizedAuthors = safeAuthors.map(author => author.toLowerCase().trim());

      // Query for books with similar title
      const booksQuery = query(
        collection(db, 'books'),
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(booksQuery);
      
      const existingBooks = [];
      querySnapshot.docs.forEach(doc => {
        const book = doc.data();
        const bookTitle = (book.title || 'Unknown Title').toLowerCase().trim();
        const safeBookAuthors = Array.isArray(book.authors) ? book.authors : ['Unknown Author'];
        const bookAuthors = safeBookAuthors.map(author => author.toLowerCase().trim());
        
        // Check for exact title match or very similar title + author match
        const titleMatch = bookTitle === normalizedTitle;
        const authorMatch = normalizedAuthors.some(author => bookAuthors.includes(author));
        
        if (titleMatch && authorMatch) {
          existingBooks.push({ id: doc.id, ...book });
        }
      });

      return existingBooks;
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      return [];
    }
  }

  // Add book with duplicate checking (enhanced from iOS)
  async addBook(userId, bookData) {
    try {
      // Check for duplicates first
      const duplicates = await this.checkForDuplicates(userId, bookData.title, bookData.authors);
      
      if (duplicates.length > 0) {
        throw new Error(`This book already exists in your library: "${bookData.title}" by ${bookData.authors.join(', ')}`);
      }

      // Add the book
      const docRef = await addDoc(collection(db, 'books'), bookData);
      
      return {
        id: docRef.id,
        ...bookData
      };
    } catch (error) {
      console.error('Error adding book:', error);
      throw error;
    }
  }

  // Get user's books with duplicate detection and removal
  async getUserBooks(userId) {
    try {
      const booksQuery = query(
        collection(db, 'books'),
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(booksQuery);
      const books = [];
      const seenBooks = new Set();

      querySnapshot.docs.forEach(doc => {
        const book = { id: doc.id, ...doc.data() };
        
        // Safely handle missing or null authors
        const title = book.title || 'Unknown Title';
        const authors = Array.isArray(book.authors) ? book.authors : ['Unknown Author'];
        const bookKey = `${title.toLowerCase().trim()}_${authors.join(',').toLowerCase().trim()}`;
        
        // Only add if we haven't seen this book before
        if (!seenBooks.has(bookKey)) {
          seenBooks.add(bookKey);
          books.push(book);
        }
      });

      return books;
    } catch (error) {
      console.error('Error fetching user books:', error);
      throw error;
    }
  }

  // Remove duplicate books (cleanup utility)
  async removeDuplicateBooks(userId) {
    try {
      const booksQuery = query(
        collection(db, 'books'),
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(booksQuery);
      const books = [];
      const seenBooks = new Map();
      const duplicatesToDelete = [];

      querySnapshot.docs.forEach(doc => {
        const book = { id: doc.id, ...doc.data() };
        
        // Safely handle missing or null data
        const title = book.title || 'Unknown Title';
        const authors = Array.isArray(book.authors) ? book.authors : ['Unknown Author'];
        const bookKey = `${title.toLowerCase().trim()}_${authors.join(',').toLowerCase().trim()}`;
        
        if (seenBooks.has(bookKey)) {
          // This is a duplicate, mark for deletion
          duplicatesToDelete.push(doc.id);
        } else {
          // First occurrence, keep it
          seenBooks.set(bookKey, book);
          books.push(book);
        }
      });

      // Delete duplicates
      if (duplicatesToDelete.length > 0) {
        console.log(`Found ${duplicatesToDelete.length} duplicate books to remove`);
        // Note: In a production app, you'd want to batch delete these
        // For now, we'll just return info about duplicates found
      }

      return {
        uniqueBooks: books,
        duplicatesFound: duplicatesToDelete.length,
        duplicateIds: duplicatesToDelete
      };
    } catch (error) {
      console.error('Error removing duplicates:', error);
      throw error;
    }
  }
}

export default new BookService();